/**
 * What stands in each of a row's cells.
 *
 * Small components rather than blocks inside the table, because three of them are drawn
 * TWICE — the still, the muscle pills and the equipment tiles all appear again in the preview panel
 * and the theatre — and because each owns a fallback the row around it should not have to know
 * about: a movement with no photograph, a piece of equipment whose picture 404s, a muscle list
 * longer than the column.
 *
 * None of them takes a DTO. `model/exerciseRow` is the seam, and a component that reached past it
 * would be a second copy of the projection.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BarbellGlyph } from '~/shared/ui/ExerciseGlyphs';
import { ExerciseTypeGlyph } from '~/entities/exercise-library';
import { exerciseTypeColour } from '~/entities/exercise-library';
import photoBox from '~/shared/ui/photoBox.module.css';

import {
  EQUIPMENT_THUMBS,
  exerciseRowStatuses,
  muscleOverflow,
  type EquipmentThumb,
  type ExerciseRow,
  type MusclePill,
} from '../model/exerciseRow';

import chip from '~/shared/ui/neutralChip.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';
import still from './exerciseStill.module.css';
import styles from './ExerciseTable.module.css';

/**
 * The movement's photograph, or the drawing that stands in its place.
 *
 * **One frame at four sizes.** The table's 44 × 33 cell, the preview panel's 16:9 card and the
 * theatre's hero and similar cards are one treatment with four geometries, so the treatment is
 * `exerciseStill.module.css` and the geometry arrives as the caller's own class. Three copies of
 * this component existed before the theatre landed, each with its own `useState` and its own
 * fallback; the theatre would have been the fourth.
 *
 * `useState` rather than a branch on the url: a still the catalogue NAMES and the media root does
 * not hold is not nothing on screen — Chrome draws its broken-file mark, which is worse than the
 * drawing this replaces. Ten of the named stills 404 locally. Keyed by the row at every call site,
 * so the flag cannot carry from one movement to the next.
 */
export function ExerciseStill({
  row,
  className,
  strokeWidth = 1.6,
  eager = false,
  fallbackCaption,
}: {
  readonly row: ExerciseRow;
  /** The caller's own box: its width, its height and its corner. */
  readonly className: string;
  /** Optical compensation for the drawn size, exactly as `ExerciseGlyphs` takes it. */
  readonly strokeWidth?: number;
  /** A still the trainer is already looking at is not below the fold. */
  readonly eager?: boolean;
  /**
   * Words under the drawing when there is no photograph — „Brak okładki" where the box is big
   * enough to say it, as the card's hero does. Absent in the table's 44px cell.
   */
  readonly fallbackCaption?: string;
}) {
  const [failed, setFailed] = useState(false);

  return (
    <span className={`${String(still.frame)} ${className}`}>
      {row.stillUrl === null || failed ? (
        fallbackCaption === undefined ? (
          <BarbellGlyph className={still.glyph} strokeWidth={strokeWidth} />
        ) : (
          <span className={still.fallback}>
            <BarbellGlyph className={still.glyph} strokeWidth={strokeWidth} />
            <span className={still.caption}>{fallbackCaption}</span>
          </span>
        )
      ) : (
        <img
          className={`${String(photoBox.photo)} ${String(still.photo)}`}
          src={row.stillUrl}
          alt=""
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          onError={() => {
            setFailed(true);
          }}
        />
      )}
    </span>
  );
}

/**
 * The type's glyph and its WORD, both in the type's own hue — the only colour in a row, and the
 * only place on this screen where a hue means anything at all. No wash behind it (polish of
 * 4 November 2026): the hue is carried by the mark itself, never by a tinted box.
 */
export function ExerciseTypeMark({ row }: { readonly row: ExerciseRow }) {
  return (
    <span className={styles.typeMark} style={exerciseTypeColour(row.typeKey)}>
      <ExerciseTypeGlyph type={row.typeKey} className={styles.typeGlyph} />
      <span className={styles.typeWord}>{row.typeLabel}</span>
    </span>
  );
}

/**
 * The muscles, capped by what the layout can fit.
 *
 * `cap` is two with the preview panel open and three without — a property of the layout, which is
 * why it arrives here rather than being baked into the projection. The `+N` carries the names it
 * stands for, in its title AND in its accessible text: a bare figure beside two words is a number a
 * trainer cannot act on.
 *
 * ── PRIMARY and SECONDARY are told apart by FILL, which a listener cannot hear ───────────────
 * The two pills differ by their ground and by nothing else, so a screen reader read three muscle
 * names with no way to know which of them the movement actually trains — the one thing the column
 * exists to say. Each pill carries the distinction as sr-only text, in the phone's own two words,
 * which is the pair the theatre's rail already prints as visible labels. Nothing changes on screen.
 */
export function ExerciseMuscles({
  muscles,
  cap,
}: {
  readonly muscles: readonly MusclePill[];
  readonly cap: number;
}) {
  const { t } = useTranslation();
  const overflow = muscleOverflow(muscles, cap);

  if (muscles.length === 0) {
    return <span className={styles.more}>{t('web:exerciseLibrary.row.noMuscles')}</span>;
  }

  const hiddenNames = overflow.hiddenNames.join(', ');
  return (
    <span className={styles.pills}>
      {overflow.shown.map((pill) => (
        <span
          key={pill.group}
          className={`${String(styles.pill)} ${pill.secondary ? String(styles.pillSecondary) : ''}`}
        >
          {pill.label}
          <span className={hidden.srOnly}>
            {' '}
            {t(
              pill.secondary
                ? 'exerciseLibrary.detail.musclesSecondary'
                : 'exerciseLibrary.detail.musclesPrimary',
            )}
          </span>
        </span>
      ))}
      {overflow.hidden > 0 && (
        <span
          className={styles.more}
          title={t('web:exerciseLibrary.row.moreMuscles', { names: hiddenNames })}
        >
          <span aria-hidden>+{overflow.hidden}</span>
          <span className={hidden.srOnly}>
            {t('web:exerciseLibrary.row.moreMuscles', { names: hiddenNames })}
          </span>
        </span>
      )}
    </span>
  );
}

/**
 * One piece of equipment's thumb: its photograph, or its initial when there is none.
 *
 * A component rather than a branch, for the one `useState` — the same fallback, for the same
 * reason, that the still above carries.
 */
function EquipmentDisc({ piece }: { readonly piece: EquipmentThumb }) {
  const [failed, setFailed] = useState(false);

  return (
    <span className={styles.thumb} title={piece.name}>
      {piece.imageUrl === null || failed ? (
        <i className={styles.thumbLetter} aria-hidden>
          {piece.name.slice(0, 1).toUpperCase()}
        </i>
      ) : (
        <img
          className={`${String(photoBox.photo)} ${String(styles.thumbPhoto)}`}
          src={piece.imageUrl}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => {
            setFailed(true);
          }}
        />
      )}
    </span>
  );
}

/**
 * Up to three square thumbs and a `+N`.
 *
 * The discs are pictures and carry no text, so the whole list is spoken once beside them — a
 * listener otherwise hears a row whose equipment column is silent, which reads as a movement that
 * needs none. A movement that genuinely needs none says so.
 */
export function ExerciseEquipmentThumbs({ row }: { readonly row: ExerciseRow }) {
  const { t } = useTranslation();

  if (row.equipment.length === 0) {
    return <span className={styles.more}>{t('web:exerciseLibrary.row.noEquipment')}</span>;
  }

  // The cap is the ROW's, not the movement's — the preview panel draws the same list in full.
  const overflow = row.equipment.length - EQUIPMENT_THUMBS;
  return (
    <span className={styles.thumbs}>
      {row.equipment.slice(0, EQUIPMENT_THUMBS).map((piece) => (
        <EquipmentDisc key={piece.id} piece={piece} />
      ))}
      {overflow > 0 && (
        <span className={styles.thumbsMore} aria-hidden>
          +{overflow}
        </span>
      )}
      <span className={hidden.srOnly}>
        {t('web:exerciseLibrary.row.equipment', {
          names: row.equipment.map((piece) => piece.name).join(', '),
        })}
      </span>
    </span>
  );
}

/** The words a status chip prints. „Twoje" is the phone's own word. */
const STATUS_KEYS = {
  own: 'exerciseLibrary.list.yourBadge',
  review: 'web:exerciseLibrary.review.status',
} as const;

/**
 * „Twoje" and „Do uzupełnienia" after the movement's name, as `shared/ui/neutralChip` at its small
 * cut — content beside the name, so they join the row header's name. They never shrink; the name
 * before them ellipsises instead. `model/exerciseRow`'s `exerciseRowStatuses` decides which.
 */
export function ExerciseStatuses({ row }: { readonly row: ExerciseRow }) {
  const { t } = useTranslation();
  const statuses = exerciseRowStatuses(row);
  if (statuses.length === 0) return null;
  return (
    <span className={styles.statuses}>
      {statuses.map((status) => (
        <span
          key={status}
          className={`${String(chip.neutralChip)} ${String(chip.small)} ${
            status === 'review' ? String(styles.statusReview) : ''
          }`}
        >
          {t(STATUS_KEYS[status])}
        </span>
      ))}
    </span>
  );
}
